/**
 * Flotă & Șoferi — vehicles, drivers, and route dispatch with the animated
 * Bihor map as the centrepiece.
 *
 * Pick a driver + van, toggle which "Pe drum" orders ride along, and watch the
 * refrigerated truck deliver stop-by-stop across a stylised Oradea/Bihor map
 * (RouteMap — fully offline SVG). Everything reads reactively from Dexie.
 */

import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import {
  Gauge,
  MapPin,
  PackageCheck,
  Phone,
  Route as RouteIcon,
  Snowflake,
  Truck,
  User,
  Warehouse,
  Weight,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  RouteMap,
  type RouteMapHandle,
  type RouteStop,
  distOras,
  orasDinLocalitate,
} from '@/components/RouteMap'
import { ScreenHeader } from '@/components/ScreenHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { MasinaStatus, SoferStatus } from '@/db/dexie'
import { getClienti, getComenzi, getMasini, getSoferi } from '@/db/queries'
import { fmtKg, fmtNumar } from '@/lib/format'
import { cn } from '@/lib/utils'

// --- status colour maps (SPEC: pine / amber / taupe) -----------------------
const MASINA_TON: Record<MasinaStatus, { dot: string; text: string; label: string }> = {
  disponibilă: { dot: 'var(--pine)', text: 'text-pine', label: 'disponibilă' },
  'pe traseu': { dot: 'var(--amber-strong)', text: 'text-amber-ink', label: 'pe traseu' },
  service: { dot: 'var(--taupe)', text: 'text-muted-foreground', label: 'în service' },
}

const SOFER_TON: Record<SoferStatus, { dot: string; text: string; label: string }> = {
  disponibil: { dot: 'var(--pine)', text: 'text-pine', label: 'disponibil' },
  'pe traseu': { dot: 'var(--amber-strong)', text: 'text-amber-ink', label: 'pe traseu' },
  liber: { dot: 'var(--taupe)', text: 'text-muted-foreground', label: 'liber' },
}

function StatusDot({ color }: { color: string }) {
  return (
    <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden />
  )
}

function initiale(nume: string): string {
  return nume
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

/** Nearest-neighbour ordering from the Oradea depot — a tidier-looking route. */
function ordoneazaTraseu(items: RouteStop[]): RouteStop[] {
  const rest = [...items]
  const out: RouteStop[] = []
  let curent = orasDinLocalitate('Oradea')
  while (rest.length) {
    let bi = 0
    let bd = Number.POSITIVE_INFINITY
    rest.forEach((s, i) => {
      const d = distOras(curent, s.oras)
      if (d < bd) {
        bd = d
        bi = i
      }
    })
    const [next] = rest.splice(bi, 1)
    if (!next) break
    out.push(next)
    curent = next.oras
  }
  return out
}

export default function Flota() {
  const masini = useLiveQuery(getMasini, [])
  const soferi = useLiveQuery(getSoferi, [])
  const clienti = useLiveQuery(getClienti, [])
  const comenziPeDrum = useLiveQuery(() => getComenzi('Pe drum'), [])

  const routeRef = useRef<RouteMapHandle>(null)
  const initedRef = useRef(false)

  const [soferId, setSoferId] = useState<string | null>(null)
  const [masinaId, setMasinaId] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [nota, setNota] = useState<string | null>(null)

  const clientById = useMemo(() => new Map((clienti ?? []).map((c) => [c.id, c])), [clienti])
  const masinaById = useMemo(() => new Map((masini ?? []).map((m) => [m.id, m])), [masini])

  // sensible defaults once the data lands
  useEffect(() => {
    if (initedRef.current) return
    if (!comenziPeDrum || !soferi || !masini || comenziPeDrum.length === 0) return
    initedRef.current = true
    setSelected(new Set(comenziPeDrum.map((c) => c.id)))
    const cuSofer = comenziPeDrum.find((c) => c.soferId)
    const sof =
      cuSofer?.soferId ?? soferi.find((s) => s.status !== 'liber')?.id ?? soferi[0]?.id ?? null
    setSoferId(sof)
    const sofObj = soferi.find((s) => s.id === sof)
    setMasinaId(
      cuSofer?.masinaId ??
        sofObj?.masinaId ??
        masini.find((m) => m.status !== 'service')?.id ??
        masini[0]?.id ??
        null,
    )
  }, [comenziPeDrum, soferi, masini])

  const stops = useMemo<RouteStop[]>(() => {
    const raw = (comenziPeDrum ?? [])
      .filter((c) => selected.has(c.id))
      .map((c) => {
        const cl = clientById.get(c.clientId)
        return {
          id: c.id,
          eticheta: cl?.nume ?? c.clientId,
          oras: orasDinLocalitate(cl?.localitate ?? 'Oradea'),
        }
      })
    return ordoneazaTraseu(raw)
  }, [comenziPeDrum, selected, clientById])

  const selectedKg = useMemo(
    () =>
      (comenziPeDrum ?? []).filter((c) => selected.has(c.id)).reduce((s, c) => s + c.totalKg, 0),
    [comenziPeDrum, selected],
  )
  const masinaSel = masinaId ? masinaById.get(masinaId) : undefined
  const capacitate = masinaSel?.capacitateKg ?? 0
  const incarcarePct = capacitate > 0 ? Math.min(100, (selectedKg / capacitate) * 100) : 0
  const supraincarcat = capacitate > 0 && selectedKg > capacitate

  function toggleOrder(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setNota(null)
  }

  function alegeSofer(id: string) {
    setSoferId(id)
    const sof = (soferi ?? []).find((s) => s.id === id)
    if (sof?.masinaId) setMasinaId(sof.masinaId)
  }

  const soferSel = soferId ? (soferi ?? []).find((s) => s.id === soferId) : undefined

  return (
    <div>
      <ScreenHeader tab="flota">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            routeRef.current?.reset()
            setNota(null)
          }}
        >
          <RouteIcon className="size-4" />
          Reia traseul
        </Button>
      </ScreenHeader>

      {/* Dispatch + map — the centrepiece */}
      <Card className="mb-5 gap-0 overflow-hidden py-0">
        <div className="grid grid-cols-1 gap-0 xl:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
          {/* dispatch controls */}
          <div className="flex flex-col gap-5 border-b border-border p-5 xl:border-b-0 xl:border-r">
            <div>
              <h3 className="font-display text-lg font-medium">Dispecerizare traseu</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Alege șoferul și mașina, apoi opririle de pe traseu.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Șofer
                </span>
                <Select value={soferId ?? ''} onValueChange={alegeSofer}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Alege șofer" />
                  </SelectTrigger>
                  <SelectContent>
                    {(soferi ?? []).map((s) => (
                      <SelectItem key={s.id} value={s.id} disabled={s.status === 'liber'}>
                        {s.nume}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Mașină
                </span>
                <Select value={masinaId ?? ''} onValueChange={setMasinaId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Alege mașină" />
                  </SelectTrigger>
                  <SelectContent>
                    {(masini ?? []).map((m) => (
                      <SelectItem key={m.id} value={m.id} disabled={m.status === 'service'}>
                        <span className="font-mono">{m.nrInmatriculare}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* load vs capacity */}
            <div className="rounded-lg border border-border bg-secondary/40 p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Weight className="size-3.5" />
                  Încărcătură
                </span>
                <span className="nums font-mono font-medium">
                  <span className={cn(supraincarcat && 'text-tomato')}>{fmtKg(selectedKg)}</span>
                  <span className="text-muted-foreground"> / {fmtNumar(capacitate)} kg</span>
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    supraincarcat ? 'bg-tomato' : 'bg-amber',
                  )}
                  style={{ width: `${incarcarePct}%` }}
                />
              </div>
              {supraincarcat && (
                <p className="mt-1.5 text-xs text-tomato">
                  Depășește capacitatea mașinii — alege alt vehicul.
                </p>
              )}
            </div>

            {/* which orders ride along */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Opriri pe traseu ({stops.length})
              </span>
              <div className="flex flex-col gap-1.5">
                {(comenziPeDrum ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Nicio comandă „Pe drum" momentan.</p>
                )}
                {(comenziPeDrum ?? []).map((c) => {
                  const cl = clientById.get(c.clientId)
                  const on = selected.has(c.id)
                  return (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => toggleOrder(c.id)}
                      className={cn(
                        'flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                        on
                          ? 'border-amber/50 bg-amber-soft/60'
                          : 'border-border bg-card hover:bg-accent',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                            on
                              ? 'border-amber-strong bg-amber-strong text-primary-foreground'
                              : 'border-border',
                          )}
                        >
                          {on && <PackageCheck className="size-3" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {cl?.nume ?? c.clientId}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="size-3" />
                            {cl?.localitate ?? '—'}
                          </span>
                        </span>
                      </span>
                      <span className="nums shrink-0 font-mono text-xs text-muted-foreground">
                        {fmtKg(c.totalKg)}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {soferSel && (
              <div className="mt-auto flex items-center gap-2 rounded-lg bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                <Truck className="size-3.5 text-amber-ink" />
                <span>
                  <span className="font-medium text-foreground">{soferSel.nume}</span> ·{' '}
                  <span className="font-mono">{masinaSel?.nrInmatriculare ?? '—'}</span> · pornire
                  din depozit Oradea
                </span>
              </div>
            )}
          </div>

          {/* the animated map */}
          <div className="flex flex-col gap-3 bg-secondary/20 p-4 sm:p-5">
            <div className="mx-auto w-full max-w-[760px]">
              <RouteMap
                ref={routeRef}
                stops={stops}
                showControls
                onArrive={(_, stop) => setNota(`Se livrează la ${stop.eticheta}…`)}
                onComplete={() => setNota('Traseu finalizat — toate livrările au fost efectuate.')}
              />
            </div>
            <div className="mx-auto flex w-full max-w-[760px] items-center gap-2">
              <Button
                size="sm"
                onClick={() => routeRef.current?.play()}
                disabled={stops.length === 0}
              >
                <Truck className="size-4" />
                Pornește livrarea
              </Button>
              <motion.p
                key={nota ?? 'idle'}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="min-w-0 truncate text-sm text-muted-foreground"
              >
                {nota ?? 'Camion frigorific · livrare secvențială pe opriri'}
              </motion.p>
            </div>
          </div>
        </div>
      </Card>

      {/* Mașini + Șoferi */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Mașini */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="size-4 text-amber-ink" />
              Mașini
              <Badge variant="muted" className="ml-auto font-normal">
                {(masini ?? []).length} vehicule
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {(masini ?? []).map((m, i) => {
              const ton = MASINA_TON[m.status]
              const activ = m.id === masinaId
              return (
                <motion.button
                  type="button"
                  key={m.id}
                  onClick={() => m.status !== 'service' && setMasinaId(m.id)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                    activ
                      ? 'border-amber/60 bg-amber-soft/50'
                      : 'border-border bg-card hover:bg-accent',
                    m.status === 'service' && 'opacity-60',
                  )}
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-amber-ink">
                    <Truck className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="nums font-mono text-sm font-semibold">
                        {m.nrInmatriculare}
                      </span>
                      <Badge variant="success" className="gap-1 px-1.5 py-0 text-[10px]">
                        <Snowflake className="size-2.5" />
                        frigorific
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="nums font-mono">{fmtNumar(m.capacitateKg)} kg</span>
                      <span className="text-hairline">·</span>
                      <span className="flex items-center gap-1">
                        <Gauge className="size-3" />
                        <span className="nums font-mono">{fmtNumar(m.kmAzi)} km azi</span>
                      </span>
                    </div>
                  </div>
                  <span className={cn('flex items-center gap-1.5 text-xs font-medium', ton.text)}>
                    <StatusDot color={ton.dot} />
                    {ton.label}
                  </span>
                </motion.button>
              )
            })}
          </CardContent>
        </Card>

        {/* Șoferi */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="size-4 text-amber-ink" />
              Șoferi
              <Badge variant="muted" className="ml-auto font-normal">
                {(soferi ?? []).length} șoferi
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {(soferi ?? []).map((s, i) => {
              const ton = SOFER_TON[s.status]
              const activ = s.id === soferId
              const masina = s.masinaId ? masinaById.get(s.masinaId) : undefined
              return (
                <motion.button
                  type="button"
                  key={s.id}
                  onClick={() => s.status !== 'liber' && alegeSofer(s.id)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                    activ
                      ? 'border-amber/60 bg-amber-soft/50'
                      : 'border-border bg-card hover:bg-accent',
                    s.status === 'liber' && 'opacity-60',
                  )}
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-soft font-mono text-xs font-semibold text-amber-ink">
                    {initiale(s.nume)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">{s.nume}</span>
                      <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                        cat. {s.permis}
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Phone className="size-3" />
                        <span className="nums font-mono">{s.telefon}</span>
                      </span>
                      {masina && (
                        <>
                          <span className="text-hairline">·</span>
                          <span className="nums font-mono">{masina.nrInmatriculare}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className={cn('flex items-center gap-1.5 text-xs font-medium', ton.text)}>
                    <StatusDot color={ton.dot} />
                    {ton.label}
                  </span>
                </motion.button>
              )
            })}
          </CardContent>
        </Card>
      </div>

      {/* depot footnote */}
      <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
        <Warehouse className="size-3.5" />
        Depozit central: Str. Depozitelor 12, Oradea · toate mașinile sunt frigorifice (lanț de frig
        neîntrerupt).
      </div>
    </div>
  )
}
